import { ref, watch, onMounted, onBeforeUnmount } from 'vue'

/**
 * Pin a fixed-position element under a trigger element.
 *
 *   const style = useAnchor(
 *     () => isOpen.value,                        // open state
 *     () => document.querySelector('.user'),      // trigger lookup
 *     { align: 'right', offset: 6 }
 *   )
 *
 *   <div :style="style" />
 *
 * The returned style ref contains `top` and either `right` or
 * `left` — exactly the inline styles the prototype's
 * `positionPopup()` set on every open.
 */
export function useAnchor(isOpenFn, triggerFn, { align = 'right', offset = 6 } = {}) {
  const style = ref({})

  function recalc() {
    if (!isOpenFn()) return
    const trigger = triggerFn()
    if (!trigger) return
    const r = trigger.getBoundingClientRect()
    if (align === 'right') {
      style.value = {
        position: 'fixed',
        top: `${r.bottom + offset}px`,
        right: `${Math.max(8, window.innerWidth - r.right)}px`,
        left: 'auto',
      }
    } else {
      style.value = {
        position: 'fixed',
        top: `${r.bottom + offset}px`,
        left: `${r.left}px`,
        right: 'auto',
      }
    }
  }

  watch(isOpenFn, (open) => {
    if (open) requestAnimationFrame(recalc)
  })

  onMounted(() => {
    window.addEventListener('resize', recalc)
    window.addEventListener('scroll', recalc, true)
  })
  onBeforeUnmount(() => {
    window.removeEventListener('resize', recalc)
    window.removeEventListener('scroll', recalc, true)
  })

  return style
}
